import React, { useCallback, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import BodyText from 'LuxKit/Typography/BodyText'

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
  margin-bottom: ${rem(-8)};
`

const messages = defineMessages({
  freeCancellation: {
    id: 'gnc42bYCUYEC1VtZ_FBb',
    defaultMessage: 'Free cancellation',
    description: 'Form field label for free cancellation policy on experience',
  },
  instantConfirmation: {
    id: 'liiEI8aOkzbI4FNe-4v4',
    defaultMessage: 'Instant confirmation',
    description:
      'Featured filter option for instant confirmation in experience search',
  },
  isGift: {
    id: 'edMa9w6iG60jFtGtkRgD',
    defaultMessage: 'Gift inspiration',
    description:
      'Featured filter option for gift inspiration in experience search',
  },
})

const FEATURED_OPTIONS: Array<{
  value: Experiences.FeaturedFilter;
  messageKey: keyof typeof messages;
}> = [
  { value: 'free-cancellation', messageKey: 'freeCancellation' },
  { value: 'instant-confirmation', messageKey: 'instantConfirmation' },
  { value: 'is-gift', messageKey: 'isGift' },
]

interface Props {
  featuredCounts?: Record<Experiences.FeaturedFilter, number>;
}

function ExperienceSearchFeaturedFilter({ featuredCounts }: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()

  const selectedFeatured = useMemo(
    () => searchFilters.featured ?? [],
    [searchFilters.featured],
  )

  const onToggle = useCallback(
    (value: Experiences.FeaturedFilter) => {
      const next = selectedFeatured.includes(value)
        ? selectedFeatured.filter((v) => v !== value)
        : [...selectedFeatured, value]
      setSearchFilters({
        ...searchFilters,
        featured: next.length > 0 ? next : undefined,
      })
    },
    [selectedFeatured, searchFilters, setSearchFilters],
  )

  return (
    <ListContainer>
      {FEATURED_OPTIONS.map((option) => (
        <CheckboxInputListItem
          key={option.value}
          inputPlacement="start"
          checked={selectedFeatured.includes(option.value)}
          onChange={() => onToggle(option.value)}
          title={intl.formatMessage(messages[option.messageKey])}
          size="medium"
          disabled={featuredCounts?.[option.value] === 0}
        >
          {featuredCounts?.[option.value] !== undefined && (
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'nKP4Celjiz8b_TNHDtEU',
                  defaultMessage: '({count})',
                  description:
                    'Count of matching experiences shown next to a featured filter option',
                },
                { count: featuredCounts[option.value] },
              )}
            </BodyText>
          )}
        </CheckboxInputListItem>
      ))}
    </ListContainer>
  )
}

export default ExperienceSearchFeaturedFilter
